import '../app-button.ts';
import './app-line-chart.ts';
import { injectAesthetic } from '../shared-styles.ts';
import { MetricsService } from '../../../../services/metrics.ts';

export class AppChartExplorer extends HTMLElement {
    private isOpen = false;
    private _type: string = 'traffic';
    private _orgId: string = '';
    private _timeRange: string = '5m';
    private _customRange: any = {};
    private _filters: any = { method: '', status: '', path: '', ip: '', host: '' };
    private _fixedFilters: any = {};
    private _logLimit: number = 100;
    private _showCustomPicker = false;

    private _isLoading = false;
    private _visualData: any = null;
    private _rawLogs: any[] = [];
    private _metricsService = MetricsService.getInstance();

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    private _methodsList: any[] = [];
    private _statusCodesList: any[] = [];


    open(type: string, orgId: string, timeRange: string, customRange: any, methodsList: any[] = [], statusCodesList: any[] = [], initialFilters: Record<string, string> = {}, fixedFilters: Record<string, string> = {}) {
        this.isOpen = true;
        this.setAttribute('open', '');
        this._type = type;
        this._orgId = orgId;
        this._timeRange = timeRange;
        this._customRange = customRange;
        this._methodsList = methodsList;
        this._statusCodesList = statusCodesList;
        this._filters = { ...initialFilters };
        this._fixedFilters = { ...fixedFilters };
        
        if (type === 'error_rate' && !this._filters.status) {
            this._filters.status = '5xx';
        }

        this.render();
        this.fetchData();
        document.body.style.overflow = 'hidden';
        document.body.classList.add('explorer-open');
    }

    close() {
        this.isOpen = false;
        this.removeAttribute('open');
        this.render();
        document.body.style.overflow = '';
        document.body.classList.remove('explorer-open');
    }

    private getTimestamps() {
        let start: number | undefined;
        let end: number | undefined;
        if (this._timeRange === 'custom') {
            start = Math.floor(new Date(this._customRange.start).getTime() / 1000);
            end = Math.floor(new Date(this._customRange.end).getTime() / 1000);
        }
        return { start, end };
    }

    private async fetchData(isLoadMore = false) {
        if (!isLoadMore) {
            this._isLoading = true;
            this.updateContent();
        } else {
            const btn = this.shadowRoot?.querySelector('#load-more-btn');
            if (btn) btn.innerHTML = 'Loading...';
        }

        const { start, end } = this.getTimestamps();

        let visualMetricType = 'traffic_history';
        if (this._type === 'methods') visualMetricType = 'methods';
        if (this._type === 'status_codes') visualMetricType = 'status_codes';
        if (this._type === 'routes') visualMetricType = 'routes';


        const activeFilters = { ...this._filters, ...this._fixedFilters };

        // Fetch visual data
        const logsPromise = this._metricsService.getMetric('raw_logs', this._orgId, this._timeRange, start, end, activeFilters, this._logLimit);
        
        let visualPromise;
        if (visualMetricType === 'traffic_history') {
            // Add interval=60 for per-minute granularity

            visualPromise = this._metricsService.getMetric(visualMetricType, this._orgId, this._timeRange, start, end, activeFilters, undefined, 60);
        } else {
            visualPromise = this._metricsService.getMetric(visualMetricType, this._orgId, this._timeRange, start, end, activeFilters);
        }

        const [visualRes, logsRes] = await Promise.all([visualPromise, logsPromise]);

        const scrollEl = this.shadowRoot?.querySelector('.content-scroll');
        const prevScroll = scrollEl?.scrollTop || 0;

        // The API client already unwraps { data: ... } responses, so the result
        // may already be the raw array. Try .data first, then fall back.
        if (Array.isArray(visualRes)) {
            this._visualData = visualRes;
        } else if (visualRes?.data && Array.isArray(visualRes.data)) {
            this._visualData = visualRes.data;
        } else {
            this._visualData = null;
        }

        if (Array.isArray(logsRes)) {
            this._rawLogs = logsRes;
        } else if (logsRes?.data && Array.isArray(logsRes.data)) {
            this._rawLogs = logsRes.data;
        } else {
            this._rawLogs = [];
        }

        this._isLoading = false;
        this.updateContent();

        if (isLoadMore && scrollEl) {
            requestAnimationFrame(() => {
                scrollEl.scrollTop = prevScroll;
            });
        }
    }

    private updateContent() {
        const scrollEl = this.shadowRoot?.querySelector('.content-scroll');
        if (!scrollEl) return;

        const formatBytes = (bytes: number) => {
            if (bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        };

        scrollEl.innerHTML = this._isLoading ? `
            <div class="loader">
                <div style="width: 40px; height: 40px; border: 3px solid rgba(245, 158, 11, 0.1); border-top-color: var(--accent); border-radius: 50%; animation: spin 1s linear infinite;"></div>
            </div>
        ` : `
            <!-- VISUALIZER -->
            <div class="visualizer-section">
                <div style="flex: 1; position: relative; min-height: 0; overflow: hidden;">
                    ${this.renderVisualizer()}
                </div>
            </div>

            <!-- DATA GRID -->
            <div class="grid-section">
                <div class="grid-header">
                    <div style="font-size: 0.75rem; color: var(--text-dim); text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em;">Raw Logs (${this._rawLogs.length}${this._rawLogs.length === 100 ? '+' : ''})</div>
                    <app-button variant="ghost" size="sm" id="export-btn" icon="download">Export CSV</app-button>
                </div>
                <div class="data-grid">
                    <table class="grid-table">
                        <thead>
                            <tr>
                                <th style="width: 180px;">Timestamp</th>
                                <th style="width: 80px;">Method</th>
                                <th>Path</th>
                                <th style="width: 80px;">Status</th>
                                <th style="width: 90px;">Duration</th>
                                <th style="width: 90px;">Bytes</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._rawLogs.map((log: any, i) => `
                                <tr class="explorable-row" data-index="${i}">
                                    <td style="color: var(--text-dim); font-family: var(--font-mono); font-size: 0.75rem;">${new Date(log.timestamp).toLocaleString()}</td>
                                    <td><span class="method-badge">${(log.method || '').replace(/</g, '&lt;')}</span></td>
                                    <td style="font-family: var(--font-mono); color: var(--accent);">${(log.path || '').replace(/</g, '&lt;')}</td>
                                    <td class="${log.status >= 500 ? 'status-err' : (log.status >= 400 ? 'status-warn' : 'status-ok')}">${log.status}</td>
                                    <td>${log.duration_ms}ms</td>
                                    <td>${formatBytes(log.bytes_total)}</td>
                                </tr>
                                <tr class="expanded-details" id="details-${i}" style="display: none;">
                                    <td colspan="6">
                                        <div style="padding: 16px; background: rgba(0,0,0,0.2); border-radius: 8px; font-family: monospace; font-size: 0.8rem; display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                                            <div><strong>Request ID:</strong> ${(log.request_id || 'N/A').replace(/</g, '&lt;')}</div>
                                            <div><strong>Host:</strong> ${(log.host || 'N/A').replace(/</g, '&lt;')}</div>
                                            <div><strong>Client IP:</strong> ${(log.client_ip || 'N/A').replace(/</g, '&lt;')}</div>
                                            <div><strong>GeoIP:</strong> ${(log.geoip_country || 'N/A').replace(/</g, '&lt;')}</div>
                                            <div><strong>User Agent:</strong> ${(log.user_agent || 'N/A').replace(/</g, '&lt;')}</div>
                                            <div><strong>Bot Shield:</strong> ${(log.bot_shield_status || 'N/A').replace(/</g, '&lt;')}</div>
                                        </div>
                                    </td>
                                </tr>
                            `).join('')}
                            ${this._rawLogs.length === 0 ? `<tr><td colspan="6" style="text-align: center; padding: 48px; color: var(--text-dim);">No logs match the current filters.</td></tr>` : ''}
                        </tbody>
                    </table>
                    ${this._rawLogs.length >= this._logLimit ? `
                        <div style="text-align: center; padding: 16px; border-top: 1px solid rgba(255,255,255,0.05);">
                            <app-button id="load-more-btn" variant="ghost" size="sm">Load More (Next 100)</app-button>
                        </div>
                    ` : ''}
                </div>
            </div>
        `;

        if (!this._isLoading) {
            this.shadowRoot?.querySelector('#export-btn')?.addEventListener('click', () => this.exportCsv());
            if (this._type === 'traffic' || this._type === 'latency' || this._type === 'error_rate' || this._type === 'data_usage') {
                const chart = this.shadowRoot?.querySelector('app-line-chart') as any;
                if (chart && this._visualData) {
                    const values = this._visualData.map((d: any) => Number(d[1]));
                    const isLongRange = ['24h', '7d', '30d'].includes(this._timeRange) || (this._timeRange === 'custom' && (this.getTimestamps().end! - this.getTimestamps().start!) >= 86400);
                    
                    const { start, end } = this.getTimestamps();
                    
                    let startT = start || (Date.now()/1000 - 300); // fallback
                    let endT = end || (Date.now()/1000);
                    if (!start) {
                        const ranges: any = { '5m': 300, '30m': 1800, '1h': 3600, '24h': 86400, '7d': 604800, '30d': 2592000 };
                        startT = endT - (ranges[this._timeRange] || 300);
                    }
                    const midT = startT + ((endT - startT) / 2);

                    const formatLabel = (ts: number) => {
                        const date = new Date(ts * 1000);
                        if (isLongRange) {
                            return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        }
                        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    };

                    const labels = [formatLabel(startT), formatLabel(midT), formatLabel(endT)];
                    
                    chart.chartData = { values, labels };
                }
            }
        }
        this.bindDynamicEvents();
    }

    private exportCsv() {
        if (!this._rawLogs || this._rawLogs.length === 0) return;
        const headers = ['Timestamp', 'Method', 'Path', 'Status', 'Duration (ms)', 'Bytes'];
        const csvRows = [headers.join(',')];
        
        for (const r of this._rawLogs) {
            csvRows.push([
                new Date(r.timestamp).toISOString(),
                r.method,
                r.path,
                r.status,
                r.duration_ms,
                r.bytes_total
            ].join(','));
        }

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crate_logs_${this._orgId}_${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    render() {
        if (!this.shadowRoot) return;

        if (!this.isOpen) {
            this.shadowRoot.innerHTML = '';
            return;
        }
        
        // Ensure custom range has valid defaults
        if (!this._customRange.start) {
            const now = new Date();
            const tz = now.getTimezoneOffset() * 60000;
            this._customRange = {
                start: new Date(now.getTime() - 3600000 - tz).toISOString().slice(0, 16),
                end: new Date(now.getTime() - tz).toISOString().slice(0, 16)
            };
        }

        const titleMap: any = {
            'traffic': 'Request Volume Explorer',
            'latency': 'Latency Explorer',
            'error_rate': 'Error Rate Explorer',
            'data_usage': 'Data Transfer Explorer',
            'methods': 'Method Distribution Explorer',
            'status_codes': 'Status Distribution Explorer',
            'routes': 'Top Routes Explorer'
        };

        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    z-index: 99999;
                    font-family: var(--font-main);
                }
                .backdrop {
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(0,0,0,0.85);
                    backdrop-filter: blur(8px);
                }
                .modal {
                    position: absolute;
                    top: 2rem; left: 2rem; right: 2rem; bottom: 2rem;
                    background: #111;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 16px;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                    overflow: hidden;
                }
                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 6px 16px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    flex-shrink: 0;
                }
                .title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: var(--text-main, #fff);
                    font-family: var(--font-sketch);
                }
                .filters-bar {
                    padding: 6px 16px;
                    background: rgba(0,0,0,0.2);
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .filter-builder {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 4px;
                    background: rgba(0,0,0,0.3);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 6px;
                    padding: 4px 8px;
                    min-height: 32px;
                    box-sizing: border-box;
                    cursor: text;
                }
                .filter-builder:focus-within {
                    border-color: rgba(245, 158, 11, 0.3);
                }
                .filter-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: rgba(245, 158, 11, 0.15);
                    border: 1px solid rgba(245, 158, 11, 0.3);
                    border-radius: 4px;
                    padding: 1px 6px;
                    font-size: 0.7rem;
                    font-family: var(--font-mono);
                    color: var(--accent);
                    white-space: nowrap;
                    line-height: 1.6;
                }
                .filter-tag-key {
                    color: rgba(255,255,255,0.5);
                    font-weight: 600;
                }
                .filter-tag-remove {
                    cursor: pointer;
                    color: rgba(255,255,255,0.3);
                    font-size: 0.85rem;
                    line-height: 1;
                    padding: 0 1px;
                    transition: color 0.15s;
                }
                .filter-tag-remove:hover {
                    color: var(--accent);
                }
                .filter-builder-input {
                    flex: 1;
                    min-width: 120px;
                    background: transparent;
                    border: none;
                    color: white;
                    font-size: 0.75rem;
                    font-family: var(--font-mono);
                    outline: none;
                    padding: 2px 0;
                }
                .filter-builder-input::placeholder {
                    color: rgba(255,255,255,0.2);
                    font-family: var(--font-main);
                }
                .filter-suggestions {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: #1a1a1a;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 6px;
                    margin-top: 4px;
                    z-index: 100;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
                    max-height: 180px;
                    overflow-y: auto;
                }
                .filter-suggestion {
                    padding: 6px 12px;
                    cursor: pointer;
                    font-size: 0.75rem;
                    color: rgba(255,255,255,0.7);
                    transition: background 0.1s;
                }
                .filter-suggestion:hover, .filter-suggestion.active {
                    background: rgba(245, 158, 11, 0.1);
                    color: white;
                }
                .filter-suggestion-key {
                    color: var(--accent);
                    font-weight: 600;
                    margin-right: 6px;
                }
                .range-selector {
                    display: flex;
                    align-items: center;
                    background: rgba(0,0,0,0.3);
                    border: 1px solid rgba(255,255,255,0.06);
                    padding: 2px;
                    border-radius: 6px;
                    gap: 1px;
                    height: 32px;
                    box-sizing: border-box;
                    flex-shrink: 0;
                    position: relative;
                }
                .range-btn {
                    font-size: 0.65rem;
                    font-weight: 600;
                    font-family: var(--font-mono);
                    color: var(--text-dim);
                    background: transparent;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 32px;
                    height: 100%;
                    padding: 0 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .range-btn:hover {
                    color: white;
                    background: rgba(255,255,255,0.05);
                }
                .range-btn.active {
                    color: var(--text-main);
                    background: var(--accent);
                }
                .content-scroll {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    padding: 12px 16px;
                }
                .content-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
                .content-scroll::-webkit-scrollbar-track { background: transparent; }
                .content-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
                .content-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
                .visualizer-section {
                    position: sticky;
                    top: -12px;
                    z-index: 10;
                    height: 200px;
                    flex-shrink: 0;
                    background: rgba(17, 17, 17, 0.95);
                    backdrop-filter: blur(8px);
                    border-radius: 8px;
                    border: 1px solid rgba(255,255,255,0.05);
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 8px;
                }
                .grid-section {
                    margin-top: 8px;
                }
                .grid-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }
                .grid-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.85rem;
                    table-layout: fixed;
                }
                .grid-table th {
                    text-align: left;
                    padding: 6px 12px;
                    color: var(--text-dim);
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                    background: rgba(255,255,255,0.02);
                    font-size: 0.7rem;
                }
                .grid-table td {
                    padding: 6px 12px;
                    border-bottom: 1px solid rgba(255,255,255,0.03);
                    color: var(--text-main, #ffffff);
                }
                .explorable-row { cursor: pointer; }
                .explorable-row:hover td { background: rgba(255,255,255,0.05); }
                .status-ok { color: var(--success); }
                .status-warn { color: var(--warning); }
                .status-err { color: var(--danger); }
                .method-badge {
                    padding: 2px 6px;
                    background: rgba(255,255,255,0.1);
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-family: var(--font-mono);
                }
                .loader {
                    display: flex;
                    justify-content: center;
                    padding: 40px;
                }
                .dist-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 8px;
                }
                .dist-label { width: 100px; font-weight: 600; font-size: 0.85rem; }
                .dist-bar-wrapper { flex: 1; height: 12px; background: rgba(255,255,255,0.05); border-radius: 6px; overflow: hidden; }
                .dist-bar-fill { height: 100%; background: var(--accent); }
                .dist-val { width: 60px; text-align: right; font-size: 0.85rem; color: var(--text-dim); }
            </style>
            ${injectAesthetic('explorer')}
            <div class="backdrop" id="backdrop"></div>
            <div class="modal">
                <div class="header">
                    <div class="title">Data Explorer</div>
                    <app-button variant="ghost" size="sm" id="close-btn" icon="x"></app-button>
                </div>

                <div class="filters-bar">
                    <div class="filter-builder" id="filter-builder" style="position: relative;">
                        ${this._renderFilterTags()}
                        <input type="text" class="filter-builder-input" id="filter-input" placeholder="${this._getActiveFilterCount() > 0 ? 'Add filter...' : 'Filter by method, status, path, host, ip...'}" autocomplete="off">
                        <div class="filter-suggestions" id="filter-suggestions" style="display: none;"></div>
                    </div>


                    <div style="position: relative; display: flex; align-items: center; gap: 0.5rem;">
                        <app-time-picker value="${this._timeRange}"></app-time-picker>
                    </div>
                </div>

                <div class="content-scroll">
                    <!-- populated by updateContent -->
                </div>
            </div>
        `;
        
        this.bindStaticEvents();
        this.updateContent();
    }

    private renderVisualizer() {
        if (!this._visualData) return '<div style="color:var(--text-dim); text-align:center; padding: 40px;">No aggregate data available</div>';

        if (this._type === 'methods' || this._type === 'status_codes') {
            const total = this._visualData.reduce((acc: number, v: any) => acc + (v.value || 0), 0) || 1;
            return `
                <div style="display:flex; flex-direction:column; height: 100%; overflow-y:auto; padding-right: 16px;">
                    ${this._visualData.map((v: any) => `
                        <div class="dist-row">
                            <div class="dist-label">${v.label}</div>
                            <div class="dist-bar-wrapper">
                                <div class="dist-bar-fill" style="width: ${(v.value / total * 100).toFixed(1)}%"></div>
                            </div>
                            <div class="dist-val">${v.value.toLocaleString()}</div>
                        </div>
                    `).join('')}
                </div>
            `;
        }

        if (this._type === 'routes') {
            return `
                <div style="height: 100%; overflow-y:auto; border: 1px solid rgba(255,255,255,0.05); border-radius: 8px;">
                    <table class="grid-table">
                        <thead>
                            <tr>
                                <th style="width: 120px;">Method</th>
                                <th>Path</th>
                                <th style="width: 100px;">Status</th>
                                <th style="width: 120px; text-align:right;">Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._visualData.map((v: any) => `
                                <tr>
                                    <td><span class="method-badge">${v.method}</span></td>
                                    <td style="font-family: var(--font-mono); color: var(--accent);">${v.path}</td>
                                    <td class="${v.status >= 500 ? 'status-err' : (v.status >= 400 ? 'status-warn' : 'status-ok')}">${v.status}</td>
                                    <td style="text-align:right;">${(v.count ?? v.rps ?? v.value ?? 0).toLocaleString()}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;
        }

        // Default to line chart for traffic, latency, errors
        return `<app-line-chart chart-title="Volume"></app-line-chart>`;
    }

    private _filterKeys = ['method', 'status', 'path', 'host', 'ip'];
    private _filterLabels: Record<string, string> = { method: 'Method', status: 'Status', path: 'Path', host: 'Host', ip: 'IP' };
    private _suggestionIndex = -1;

    private _getActiveFilterCount(): number {
        return this._filterKeys.filter(k => (this._filters as any)[k]).length;
    }

    private _renderFilterTags(): string {
        return this._filterKeys
            .filter(k => (this._filters as any)[k])
            .map(k => `<span class="filter-tag"><span class="filter-tag-key">${this._filterLabels[k]}:</span>${(this._filters as any)[k]}<span class="filter-tag-remove" data-key="${k}">&times;</span></span>`)
            .join('');
    }

    private _getSuggestions(query: string): { key: string, label: string, values?: string[] }[] {
        const q = query.toLowerCase().trim();
        const suggestions: { key: string, label: string, values?: string[] }[] = [];

        for (const key of this._filterKeys) {
            if ((this._filters as any)[key]) continue; // already active
            const label = this._filterLabels[key];
            if (!q || key.includes(q) || label.toLowerCase().includes(q)) {
                if (key === 'method') {
                    suggestions.push({ key, label, values: this._methodsList.map(m => m.name || m.label || m.method || '') });
                } else if (key === 'status') {
                    suggestions.push({ key, label, values: this._statusCodesList.map(s => s.name || s.label || s.status || '') });
                } else {
                    suggestions.push({ key, label });
                }
            }
        }
        return suggestions;
    }

    private bindStaticEvents() {
        const closeBtn = this.shadowRoot?.querySelector('#close-btn');
        if (closeBtn) closeBtn.addEventListener('click', () => this.close());

        const backdrop = this.shadowRoot?.querySelector('#backdrop');
        if (backdrop) backdrop.addEventListener('click', () => this.close());

        // Filter builder logic
        const filterInput = this.shadowRoot?.querySelector('#filter-input') as HTMLInputElement;
        const suggestionsEl = this.shadowRoot?.querySelector('#filter-suggestions') as HTMLElement;
        let pendingKey = '';

        const applyFilter = (key: string, value: string) => {
            (this._filters as any)[key] = value;
            pendingKey = '';
            this._logLimit = 100;
            this.render();
            this.fetchData();
        };

        const removeFilter = (key: string) => {
            (this._filters as any)[key] = '';
            this._logLimit = 100;
            this.render();
            this.fetchData();
        };

        // Remove tag clicks
        this.shadowRoot?.querySelectorAll('.filter-tag-remove').forEach(el => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                removeFilter((el as HTMLElement).dataset.key || '');
            });
        });

        // Click on builder focuses input
        this.shadowRoot?.querySelector('#filter-builder')?.addEventListener('click', () => {
            filterInput?.focus();
        });

        if (filterInput && suggestionsEl) {
            const showSuggestions = () => {
                const query = filterInput.value;
                this._suggestionIndex = -1;

                if (pendingKey) {
                    // Show value suggestions for the selected key
                    const keyInfo = this._getSuggestions('').find(s => s.key === pendingKey);
                    if (keyInfo?.values && keyInfo.values.length > 0) {
                        const filtered = query ? keyInfo.values.filter(v => v.toLowerCase().includes(query.toLowerCase())) : keyInfo.values;
                        suggestionsEl.innerHTML = filtered.map(v =>
                            `<div class="filter-suggestion" data-value="${v}">${v}</div>`
                        ).join('') || `<div class="filter-suggestion" style="color: rgba(255,255,255,0.3);">Type a value and press Enter</div>`;
                    } else {
                        suggestionsEl.innerHTML = `<div class="filter-suggestion" style="color: rgba(255,255,255,0.3);">Type a value for ${this._filterLabels[pendingKey]} and press Enter</div>`;
                    }
                    suggestionsEl.style.display = 'block';
                } else {
                    const suggestions = this._getSuggestions(query);
                    if (suggestions.length > 0) {
                        suggestionsEl.innerHTML = suggestions.map(s =>
                            `<div class="filter-suggestion" data-key="${s.key}"><span class="filter-suggestion-key">${s.label}:</span> ${s.values ? s.values.slice(0, 3).join(', ') + (s.values.length > 3 ? '...' : '') : 'type a value'}</div>`
                        ).join('');
                        suggestionsEl.style.display = 'block';
                    } else {
                        suggestionsEl.style.display = 'none';
                    }
                }

                // Bind suggestion clicks
                suggestionsEl.querySelectorAll('.filter-suggestion').forEach(el => {
                    el.addEventListener('mousedown', (e) => {
                        e.preventDefault();
                        const key = (el as HTMLElement).dataset.key;
                        const value = (el as HTMLElement).dataset.value;
                        if (value) {
                            applyFilter(pendingKey, value);
                        } else if (key) {
                            pendingKey = key;
                            filterInput.value = '';
                            filterInput.placeholder = `Enter ${this._filterLabels[key]} value...`;
                            showSuggestions();
                        }
                    });
                });
            };

            filterInput.addEventListener('focus', showSuggestions);
            filterInput.addEventListener('input', showSuggestions);
            filterInput.addEventListener('blur', () => {
                setTimeout(() => { suggestionsEl.style.display = 'none'; }, 150);
            });

            filterInput.addEventListener('keydown', (e) => {
                const items = suggestionsEl.querySelectorAll('.filter-suggestion[data-key], .filter-suggestion[data-value]');
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    this._suggestionIndex = Math.min(this._suggestionIndex + 1, items.length - 1);
                    items.forEach((el, i) => el.classList.toggle('active', i === this._suggestionIndex));
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    this._suggestionIndex = Math.max(this._suggestionIndex - 1, 0);
                    items.forEach((el, i) => el.classList.toggle('active', i === this._suggestionIndex));
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (this._suggestionIndex >= 0 && this._suggestionIndex < items.length) {
                        const el = items[this._suggestionIndex] as HTMLElement;
                        const key = el.dataset.key;
                        const value = el.dataset.value;
                        if (value) {
                            applyFilter(pendingKey, value);
                        } else if (key) {
                            pendingKey = key;
                            filterInput.value = '';
                            filterInput.placeholder = `Enter ${this._filterLabels[key]} value...`;
                            showSuggestions();
                        }
                    } else if (pendingKey && filterInput.value.trim()) {
                        applyFilter(pendingKey, filterInput.value.trim());
                    } else {
                        // Try to parse "key:value" syntax
                        const match = filterInput.value.match(/^(\w+):\s*(.+)$/);
                        if (match) {
                            const key = match[1].toLowerCase();
                            if (this._filterKeys.includes(key)) {
                                applyFilter(key, match[2].trim());
                            }
                        }
                    }
                } else if (e.key === 'Escape') {
                    suggestionsEl.style.display = 'none';
                    pendingKey = '';
                    filterInput.value = '';
                    filterInput.placeholder = this._getActiveFilterCount() > 0 ? 'Add filter...' : 'Filter by method, status, path, host, ip...';
                } else if (e.key === 'Backspace' && filterInput.value === '') {
                    // Remove last active filter on backspace
                    const activeKeys = this._filterKeys.filter(k => (this._filters as any)[k]);
                    if (activeKeys.length > 0) {
                        removeFilter(activeKeys[activeKeys.length - 1]);
                    }
                }
            });
        }


        this.shadowRoot?.querySelector('app-time-picker')?.addEventListener('range-change', (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail.range && detail.range !== this._timeRange) {
                this._timeRange = detail.range;
                if (detail.range === 'custom' && detail.customRange) {
                    this._customRange = detail.customRange;
                }
                this._logLimit = 100;
                this.render();
                this.fetchData();
            }
        });

        this.shadowRoot?.querySelectorAll('.modal-range-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const newRange = (btn as Element).getAttribute('data-range');
                if (newRange && newRange !== this._timeRange) {
                    this._timeRange = newRange;
                    this._logLimit = 100;
                    this._showCustomPicker = false;
                    this.render();
                    this.fetchData();
                }
            });
        });

        this.shadowRoot?.querySelector('.modal-custom-btn')?.addEventListener('click', () => {
            this._showCustomPicker = !this._showCustomPicker;
            this.render();
        });

        if (this._showCustomPicker) {
            this.shadowRoot?.querySelector('.apply-range-btn')?.addEventListener('click', () => {
                this._showCustomPicker = false;
                this._timeRange = 'custom';
                this._logLimit = 100;
                this.render();
                this.fetchData();
            });
            this.shadowRoot?.querySelector('.cancel-range-btn')?.addEventListener('click', () => {
                this._showCustomPicker = false;
                this.render();
            });
            this.shadowRoot?.querySelectorAll('.datetime-input').forEach(input => {
                input.addEventListener('change', (e: any) => {
                    const field = input.getAttribute('data-field');
                    if (field === 'start') this._customRange.start = e.target.value;
                    if (field === 'end') this._customRange.end = e.target.value;
                });
            });
        }

    }

    private bindDynamicEvents() {
        // Load more logic
        this.shadowRoot?.querySelector('#load-more-btn')?.addEventListener('click', () => {
            this._logLimit += 100;
            this.fetchData(true);
        });

        // Brush selection zoom logic
        const chart = this.shadowRoot?.querySelector('app-line-chart');
        if (chart) {
            chart.addEventListener('brush-selection', (e: Event) => {
                const ce = e as CustomEvent;
                if (this._visualData && ce.detail) {
                    const startIndex = ce.detail.startIndex;
                    const endIndex = ce.detail.endIndex;
                    
                    if (startIndex >= 0 && endIndex < this._visualData.length && startIndex < endIndex) {
                        const startTs = this._visualData[startIndex][0];
                        const endTs = this._visualData[endIndex][0];
                        
                        const startD = new Date(startTs * 1000);
                        const endD = new Date(endTs * 1000);
                        const tz = startD.getTimezoneOffset() * 60000;
                        
                        this._timeRange = 'custom';
                        this._customRange = {
                            start: new Date(startD.getTime() - tz).toISOString().slice(0, 16),
                            end: new Date(endD.getTime() - tz).toISOString().slice(0, 16)
                        };
                        this._logLimit = 100;
                        this.render();
                        this.fetchData();
                    }
                }
            });
        }

        // Expandable row logic
        this.shadowRoot?.querySelectorAll('.explorable-row').forEach(row => {
            row.addEventListener('click', () => {
                const index = row.getAttribute('data-index');
                const details = this.shadowRoot?.querySelector(`#details-${index}`) as HTMLElement;
                if (details) {
                    details.style.display = details.style.display === 'none' ? 'table-row' : 'none';
                }
            });
        });
    }
}

if (!customElements.get('app-chart-explorer')) {
    customElements.define('app-chart-explorer', AppChartExplorer);
}
